import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  signal,
} from '@angular/core';
import { ReplaySubject } from 'rxjs';

import { BadgingService } from 'app/modules/social/services/badging/badging.service';
import { Loggable } from 'app/utils/logging/loggable';
import BadgeQuestion from 'entity/BadgeQuestion';
import StandardQuestion from 'entity/StandardQuestion';
import BadgeQuestionType from 'enums/BadgeQuestionType';
import QuestionVisibility from 'enums/QuestionVisibility';

interface GroupQuestion {
  title: string;
  children: QuestionSelectable[];
}

interface QuestionSelectable extends StandardQuestion {
  active: boolean;
  required: boolean;
  isFeatured: boolean;
  featured: boolean;
}

@Component({
  selector: 'app-questions',
  templateUrl: './questions.component.html',
  styleUrl: './questions.component.scss',
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class QuestionsComponent extends Loggable implements OnInit, OnDestroy {
  @Input() public roomQuestions: BadgeQuestion[] = [];
  @Output() public standardQuestions = new EventEmitter<BadgeQuestion[]>();
  private destroyed$ = new ReplaySubject<boolean>(1);
  public loading = signal(false);
  protected groups = signal<GroupQuestion[]>([
    {
      title: 'Scoot Profile',
      children: [],
    },
    {
      title: 'Additional Info',
      children: [],
    },
  ]);

  public constructor(private badgingService: BadgingService) {
    super();
  }

  public async ngOnInit(): Promise<void> {
    await this.getStandardQuestions();
  }

  public async getStandardQuestions() {
    this.loading.set(true);
    try {
      const questions = await this.badgingService.getStandardBadgeQuestions();
      this.mapStandardQuestion(questions);
      this.sendQuestion();
    } catch (err) {
      this.error('Error while getting standard questions', err);
    } finally {
      this.loading.set(false);
    }
  }

  private mapStandardQuestion(questions: StandardQuestion[]) {
    questions.forEach((question) => {
      const currentQuestion = this.roomQuestions.find((roomQuestion) => roomQuestion.title === question.title);

      const questionMapped = {
        ...question,
        active: !!currentQuestion,
        required: (currentQuestion && currentQuestion.required) || this.isQuestionTypeProfile(question) || false,
        isFeatured: question.type === 1,
        featured: (currentQuestion && currentQuestion.featured) || false,
      };

      if (question.type === 0) {
        this.groups.update((groups) => {
          groups[0].children.push(questionMapped);
          return groups;
        });
      }

      if (question.type === 1) {
        this.groups.update((groups) => {
          groups[1].children.push(questionMapped);
          return groups;
        });
      }
    });
  }

  protected sendQuestion() {
    const flatGroups = this.groups().flatMap((group) => group.children);

    const questions: BadgeQuestion[] = flatGroups
      .filter((question) => question.active)
      .map((question) => {
        return {
          title: question.title,
          type: question.type,
          inputType: question.inputType,
          description: question.description,
          required: question.required,
          featured: question.featured,
          visibility: QuestionVisibility.ALL,
        };
      });

    this.standardQuestions.emit(questions);
  }

  public isQuestionTypeProfile(question?: StandardQuestion) {
    if (!question) {
      return false;
    }
    if (question.title === 'LinkedIn' || question.title === 'About Me') {
      return false;
    }
    return question.type === BadgeQuestionType.PROFILE;
  }

  public ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }
}
